import {
    Button,
    Group,
    Modal,
    Paper,
    Progress,
    Stack,
    Text,
    Title,
    Alert,
    Divider
} from '@mantine/core';
import {
    AiOutlineClose,
    AiOutlineCheckCircle,
    AiOutlineExclamationCircle,
    AiOutlineClockCircle,
    AiOutlineStop
} from 'react-icons/ai';
import { ImportProgress } from '../services/BulkImportService';

export interface ImportProgressModalProps {
    isOpen: boolean;
    progress: ImportProgress;
    onClose: () => void;
    onCancel?: () => void;
    canCancel?: boolean;
}

export const ImportProgressModal = ({
    isOpen,
    progress,
    onClose,
    onCancel,
    canCancel = true
}: ImportProgressModalProps) => {
    const progressPercentage = progress.total > 0 ? (progress.processed / progress.total) * 100 : 0;
    const isComplete = progress.isComplete;
    const hasErrors = progress.failed > 0;

    const handleCancel = () => {
        if (onCancel && canCancel && !isComplete) {
            onCancel();
        }
    };

    const getProgressColor = () => {
        if (isComplete) {
            return hasErrors ? 'orange' : 'green';
        }
        return 'blue';
    };

    const getStatusIcon = () => {
        if (isComplete) {
            return hasErrors ? <AiOutlineExclamationCircle size={20} /> : <AiOutlineCheckCircle size={20} />;
        }
        return <AiOutlineClockCircle size={20} />;
    };

    const getStatusText = () => {
        if (isComplete) {
            if (hasErrors) {
                return `Import completed with ${progress.failed} error${progress.failed !== 1 ? 's' : ''}`;
            }
            return 'Import completed successfully';
        }
        return 'Import in progress...';
    };

    return (
        <Modal
            opened={isOpen}
            onClose={onClose}
            title={
                <Group gap="sm">
                    {getStatusIcon()}
                    <Title order={4}>Domain Import Progress</Title>
                </Group>
            }
            size="md"
            closeOnClickOutside={false}
            closeOnEscape={isComplete}
            withCloseButton={isComplete}
        >
            <Stack gap="md">
                {/* Progress Bar */}
                <Paper p="md" withBorder>
                    <Stack gap="sm">
                        <Group justify="space-between">
                            <Text size="sm" fw={500}>
                                Overall Progress
                            </Text>
                            <Text size="sm" c="dimmed">
                                {progress.processed} of {progress.total}
                            </Text>
                        </Group>
                        
                        <Progress
                            value={progressPercentage}
                            color={getProgressColor()}
                            size="lg"
                            radius="sm"
                            animated={!isComplete}
                        />
                        
                        <Text size="xs" c="dimmed" ta="center">
                            {progressPercentage.toFixed(1)}% complete
                        </Text>
                    </Stack>
                </Paper>

                {/* Current Domain Being Processed */}
                {progress.currentDomain && !isComplete && (
                    <Alert
                        icon={<AiOutlineClockCircle />}
                        color="blue"
                        variant="light"
                    >
                        <Text size="sm">
                            Currently processing: <Text span fw={500}>{progress.currentDomain}</Text>
                        </Text>
                    </Alert>
                )}

                {/* Statistics */}
                <Paper p="md" withBorder>
                    <Stack gap="xs">
                        <Text size="sm" fw={500} mb="xs">Import Statistics</Text>
                        
                        <Group justify="space-between">
                            <Group gap="xs">
                                <AiOutlineCheckCircle color="green" />
                                <Text size="sm">Successful</Text>
                            </Group>
                            <Text size="sm" fw={500} c="green">
                                {progress.successful}
                            </Text>
                        </Group>

                        <Group justify="space-between">
                            <Group gap="xs">
                                <AiOutlineExclamationCircle color="red" />
                                <Text size="sm">Failed</Text>
                            </Group>
                            <Text size="sm" fw={500} c="red">
                                {progress.failed}
                            </Text>
                        </Group>

                        <Group justify="space-between">
                            <Group gap="xs">
                                <AiOutlineClockCircle color="gray" />
                                <Text size="sm">Remaining</Text>
                            </Group>
                            <Text size="sm" fw={500} c="dimmed">
                                {progress.total - progress.processed}
                            </Text>
                        </Group>
                    </Stack>
                </Paper>

                {/* Status Message */}
                <Alert
                    color={isComplete ? (hasErrors ? 'orange' : 'green') : 'blue'}
                    variant="light"
                    icon={getStatusIcon()}
                >
                    <Text size="sm">{getStatusText()}</Text>
                </Alert>

                {/* Action Buttons */}
                <Divider />
                
                <Group justify="flex-end" gap="sm">
                    {!isComplete && canCancel && (
                        <Button
                            variant="outline"
                            color="red"
                            leftSection={<AiOutlineStop />}
                            onClick={handleCancel}
                        >
                            Cancel Import
                        </Button>
                    )}
                    
                    {isComplete && (
                        <Button
                            variant="filled"
                            leftSection={<AiOutlineClose />}
                            onClick={onClose}
                        >
                            Close
                        </Button>
                    )}
                </Group>
            </Stack>
        </Modal>
    );
};